import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN, internalUrl } from './siteConfig';
import { META_ECOMMERCE, applyPageMeta } from './seoMeta';

const SCHEMA_ID = 'webcodecraft-ecommerce-schema';

function EcommerceWebAppsPage() {
  const jsonLd = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            '@id': `${META_ECOMMERCE.canonical}#webpage`,
            url: META_ECOMMERCE.canonical,
            name: META_ECOMMERCE.title,
            description: META_ECOMMERCE.description,
            isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
            inLanguage: 'en-GB'
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
              { '@type': 'ListItem', position: 2, name: 'E-commerce & web apps', item: META_ECOMMERCE.canonical }
            ]
          }
        ]
      }),
    []
  );

  useEffect(() => {
    const restoreMeta = applyPageMeta(META_ECOMMERCE);
    const existing = document.getElementById(SCHEMA_ID);
    if (!existing) {
      const el = document.createElement('script');
      el.id = SCHEMA_ID;
      el.type = 'application/ld+json';
      el.textContent = jsonLd;
      document.head.appendChild(el);
    }
    return () => {
      restoreMeta();
      const script = document.getElementById(SCHEMA_ID);
      if (script) script.remove();
    };
  }, [jsonLd]);

  return (
    <>
      <Header />
      <main id="main-content" className="pt-16 lg:pt-20 min-w-0 overflow-x-clip">
        <section className="relative py-16 sm:py-20 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
              backgroundSize: '44px 44px'
            }}
          />
          <div className="container mx-auto px-4 relative max-w-3xl">
            <m.h1
              className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              E-commerce &amp; custom web apps
            </m.h1>
            <m.p
              className="text-lg text-slate-300 leading-relaxed mb-6"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.06 }}
            >
              {META_ECOMMERCE.description}
            </m.p>
            <m.div
              className="flex flex-wrap gap-3"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.12 }}
            >
              <Link
                to="/#contact"
                className="inline-flex items-center rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-sm hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                Discuss your build
              </Link>
              <Link
                to="/#portfolio"
                className="inline-flex items-center rounded-lg border border-white/20 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                View portfolio
              </Link>
              <a
                href={internalUrl('services')}
                className="inline-flex items-center rounded-lg border border-white/20 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                Pay-monthly plans
              </a>
            </m.div>
          </div>
        </section>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default EcommerceWebAppsPage;
